import React, { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import type { CreateCruiseItemViewModel } from './models'
import type { CreateItemProps } from './utils'
import { createCruisePayloadFromViewModel } from './utils'
import { formOptions } from '../Common/Fields/config'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import CruiseFields from 'tripPlanner/components/TripModal/TripItems/Common/CruiseFields'
import { useCreateTripItem } from 'tripPlanner/hooks/api/tripItem'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import ModalFooter from 'LuxKit/Modal/ModalFooter'

type ViewModel = CreateCruiseItemViewModel

function CreateCruise(props: CreateItemProps) {
  const { tripId, defaultDate } = props
  const intl = useIntl()

  const { closeModal, goBack } = useModalCallbacks()

  const methods = useForm<ViewModel>({
    ...formOptions,
    defaultValues: {
      type: 'CRUISE',
      sourceType: 'NA',
      isBooked: false,
      startDate: defaultDate,
      endDate: defaultDate,
      startPlace: {},
      endPlace: {},
    },
  })
  const { handleSubmit } = methods

  const { mutate: createMutate, isLoading } = useCreateTripItem({
    onSuccess: () => {
      closeModal()
    },
  })

  const createTourCruiseItem = useCallback(
    (viewModel: ViewModel) => {
      createMutate({
        tripId,
        tripItem: createCruisePayloadFromViewModel(viewModel),
      })
    },
    [createMutate, tripId],
  )

  const modalTitle = intl.formatMessage({
    id: 'R14mg6MPx_iqh_W0Bzku',
    defaultMessage: 'Add cruise',
    description: 'Modal title when adding cruise to trip in the cruise offer or booking section',
  })
  const saveLabel = intl.formatMessage({
    id: '9ynjb8-UI8KJz-R2yK-_',
    defaultMessage: 'Save',
    description: 'Save button label in the add cruise modal in the cruise section',
  })

  return (
    <>
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <FormProvider {...methods}>
        <ModalBody>
          <ModalContent>
            <CruiseFields tripId={tripId} />
          </ModalContent>
        </ModalBody>

        <ModalFooter
          primaryActionProps={{
            onClick: handleSubmit(createTourCruiseItem),
            children: saveLabel,
            disabled: isLoading,
          }}
        />
      </FormProvider>

      {isLoading && <LoadingIndicator floating />}
    </>
  )
}

export default CreateCruise
